import React, { useCallback } from "react";
import { InlineStack, Button, Text, Tooltip } from "@medentic/polaris-react";
import type { IconProps, TextProps } from "@medentic/polaris-react";
import { ReceiptIcon } from "@medentic/polaris-icons";

interface LabelWithDownloadProps {
  /** The main label/value to display */
  label: React.ReactNode;
  /** Optional download URL - if provided, shows download button */
  downloadLink?: string;
  /** Tooltip text shown over the download button */
  tooltip?: string;
  /** Icon for the download button */
  icon?: IconProps["source"];
  /** Text variant for the label */
  textVariant?: TextProps["variant"];
  /** Text tone for the label */
  textTone?: TextProps["tone"];
  /** If true, places the icon at the start (left). Defaults to false (icon at end/right) */
  iconAtStart?: boolean;
  /** If true, truncates the label text with ellipsis */
  truncate?: boolean;
}

export default function LabelWithDownload({
  label,
  downloadLink,
  tooltip,
  icon = ReceiptIcon,
  textVariant = "bodySm",
  textTone = "base",
  iconAtStart = false,
  truncate = false,
}: LabelWithDownloadProps) {
  const handleDownload = useCallback(() => {
    if (downloadLink) {
      window.open(downloadLink, "_blank");
    }
  }, [downloadLink]);

  const labelContent = truncate ? (
    <div
      style={{
        minWidth: 0,
        flex: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <Text as="span" variant={textVariant} tone={textTone}>
        {label}
      </Text>
    </div>
  ) : (
    <Text as="span" variant={textVariant} tone={textTone}>
      {label}
    </Text>
  );

  // Show icon button if there's a downloadLink OR just a tooltip (for info/note display)
  const showIconButton = downloadLink || tooltip;

  const iconButton = showIconButton ? (
    <div onClick={(event) => event.stopPropagation()}>
      <Button
        variant="tertiary"
        onClick={downloadLink ? handleDownload : undefined}
        icon={icon}
        accessibilityLabel={tooltip || "Download"}
      />
    </div>
  ) : null;

  const buttonWithTooltip =
    iconButton && tooltip ? (
      <Tooltip content={tooltip}>{iconButton}</Tooltip>
    ) : (
      iconButton
    );

  return (
    <InlineStack
      gap="200"
      blockAlign="center"
      align={iconAtStart ? "start" : "end"}
      wrap={false}
    >
      {iconAtStart ? (
        <>
          {buttonWithTooltip}
          {labelContent}
        </>
      ) : (
        <>
          {labelContent}
          {buttonWithTooltip}
        </>
      )}
    </InlineStack>
  );
}

